import tw from 'twin.macro'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty, sortBy } from 'lodash'
import { FiAlertTriangle } from 'react-icons/fi'

import { useListenCoachVideosQuery } from 'modules/VideoLibrary/videoLibraryApi'
import { useListenWktDurationInflationEnabledQuery } from 'modules/Account/accountApi'

import { Tooltip } from 'common/components/Tooltip/Tooltip'
import { Skeleton } from 'common/components/Skeleton/Skeleton'
import { getReadableTime, getTimeDurationString } from 'common/utils/timeDuration'
import {
  isTooShortToShow,
  calculateWorkoutEstimate,
  getInflationEnabled,
  LOW_CONFIDENCE_SCORE_THRESHOLD,
  getMobileDisplayTime,
  getMobileExactMinutes,
} from 'common/utils/wktDurationUtils'
import { useCustomization } from 'common/contexts/Customization/useCustomization'
import { useListenExercisesQuery } from 'modules/ExerciseLibrary/exerciseLibraryApi'
import { Dialog, DialogContent } from 'common/components/Dialog/Dialog'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { maybePluralize } from 'common/utils/stringUtils'
import EditWktDurationForm from './EditWktDurationForm/EditWktDurationForm'
import { useListenBenchmarksQuery } from '../programApi'
import { getMultiWktDurationEst } from '../programSlice'

function WorkoutDuration({ workout, orgId, selectedWktId, multiWtkIds }) {
  if (workout?.type === 'workout') {
    const isMultiWktOverview = multiWtkIds?.length > 0 && !selectedWktId
    if (isMultiWktOverview) {
      return <MultiWktDuration orgId={orgId} multiWtkIds={multiWtkIds} />
    } else {
      return <WktDuration orgId={orgId} workout={workout} />
    }
  }

  if (workout?.type === 'single_video') {
    return <VidWktDuration orgId={orgId} workout={workout} />
  }

  // A rest day gets no duration chip — there is nothing to estimate.
  return null
}

function WktDuration({ orgId, workout = {} }) {
  const { exVidOrientation } = useCustomization()
  const { data: orgWktDurationInflationEnabled } = useListenWktDurationInflationEnabledQuery({ coachOrgId: orgId })
  const { data: orgBenchmarks } = useListenBenchmarksQuery({ orgId })
  const { data: exData = {} } = useListenExercisesQuery({ coachOrgId: orgId, exVidOrientation })
  const { exercises = {} } = exData
  const exercisesLoading = exercises === undefined || exData?.isFiller
  const orgBenchmarksLoading = orgBenchmarks === undefined || orgBenchmarks?.isLoading
  const orgExercises = useMemo(
    () => (exercisesLoading || exercises === null ? {} : exercises),
    [exercises, exercisesLoading]
  )
  const inflationEnabled = getInflationEnabled({
    wktDurationInflationEnabled: workout?.enableWktDurationInflation,
    orgWktDurationInflationEnabled,
  })
  // Computed every render on purpose: the builder's data layer can update the
  // workout's contents without replacing the object, so memoizing on its
  // identity served a stale total forever (day box 83min vs modal 48min on
  // identical data). The calculation over a workout's exercises is trivial.
  const {
    totalSeconds,
    hasLowConfidenceEx,
    isLowConfidence: calculatedIsLowConfidence,
    lowConfidenceReason,
  } = calculateWorkoutEstimate({
    workout,
    orgExercises,
    orgBenchmarks,
    inflationEnabled,
  })

  const wasReviewed =
    Boolean(workout?.verifiedDurationEst) && workout?.verifiedDurationEst === getTimeDurationString(totalSeconds * 1000)
  const isLowConfidence = wasReviewed
    ? false
    : workout?.durationEstSource === 'script' &&
        workout?.confidenceScore != null &&
        workout.confidenceScore < LOW_CONFIDENCE_SCORE_THRESHOLD
      ? true
      : calculatedIsLowConfidence
  // The day box shows what members will see (Vince 8.25): the rounded display
  // for auto estimates, exact minutes for a coach's total override. The exact
  // figure lives in the edit modal's footer.
  // Under three minutes members see no estimate (MIN_WKT_DURATION_SECONDS), so the box shows a
  // dash rather than a figure nobody will be shown. A coach's own total is always shown.
  const tooShort = !workout?.durationEstOverride && isTooShortToShow(totalSeconds)
  const readableTime = workout?.durationEstOverride
    ? getMobileExactMinutes(totalSeconds || 60)
    : tooShort
      ? '—'
      : getMobileDisplayTime(totalSeconds || 60)
  const [editWktDurationModalOpen, setEditWktDurationModalOpen] = useState(false)

  const isScriptDurationSource = workout?.durationEstSource === 'script'

  return (
    <>
      <WorkoutDurationBox
        duration={readableTime}
        isEditable={true}
        isLoading={exercisesLoading || orgBenchmarksLoading || isEmpty(workout)}
        onPressEdit={() => setEditWktDurationModalOpen(true)}
        hasLowConfidenceEx={hasLowConfidenceEx}
        isScriptDurationSource={isScriptDurationSource}
        isLowConfidence={tooShort ? false : isLowConfidence}
        lowConfidenceReason={lowConfidenceReason}
        tooShort={tooShort}
      />
      <Dialog open={editWktDurationModalOpen} setOpen={setEditWktDurationModalOpen}>
        <DialogContent header='Edit workout duration'>
          {/* Mounted only while open: the form snapshots the workout into local
              state on mount, so a persistent mount would keep showing the state
              from its FIRST open — stale exercises, stale totals — no matter
              what changed in the builder since. */}
          {editWktDurationModalOpen && (
            <FormRefsControlProvider>
              <EditWktDurationForm
                orgId={orgId}
                workout={workout}
                orgExercises={orgExercises}
                orgBenchmarks={orgBenchmarks}
                setEditWktDurationModalOpen={setEditWktDurationModalOpen}
                orgWktDurationInflationEnabled={orgWktDurationInflationEnabled}
              />
            </FormRefsControlProvider>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

function MultiWktDuration({ orgId, multiWtkIds }) {
  const { exVidOrientation } = useCustomization()
  const { data: orgWktDurationInflationEnabled } = useListenWktDurationInflationEnabledQuery({ coachOrgId: orgId })
  const { data: orgBenchmarks } = useListenBenchmarksQuery({ orgId })
  const { data: videoData, isLoading } = useListenCoachVideosQuery({ coachOrgId: orgId })
  const videosLoading = isLoading || videoData?.isLoading
  const videos = useMemo(() => (videosLoading || videoData === null ? {} : videoData), [videoData, videosLoading])
  const { data: exData = {} } = useListenExercisesQuery({ coachOrgId: orgId, exVidOrientation })
  const { exercises = {} } = exData
  const exercisesLoading = exercises === undefined || exData?.isFiller
  const orgBenchmarksLoading = orgBenchmarks === undefined || orgBenchmarks?.isLoading

  const orgExercises = useMemo(
    () => (exercisesLoading || exercises === null ? {} : exercises),
    [exercises, exercisesLoading]
  )

  const extraData = useMemo(
    () => ({ orgExercises, orgBenchmarks, orgWktDurationInflationEnabled, videos }),
    [orgExercises, orgBenchmarks, orgWktDurationInflationEnabled, videos]
  )
  const { totalMultiWktSeconds, lowConfMultiWktIdxs, isMultiWktsLoading } = useSelector((state) =>
    getMultiWktDurationEst(state, multiWtkIds, extraData)
  )

  const readableTime = getMobileDisplayTime(totalMultiWktSeconds || 60)
  return (
    <WorkoutDurationBox
      duration={readableTime}
      isEditable={false}
      isLoading={exercisesLoading || orgBenchmarksLoading || videosLoading || isMultiWktsLoading}
      hasLowConfidenceEx={lowConfMultiWktIdxs?.length > 0}
      lowConfMultiWktIdxs={lowConfMultiWktIdxs}
      isMultiWkt={true}
    />
  )
}

function VidWktDuration({ orgId, workout }) {
  const { data: videoData, isLoading } = useListenCoachVideosQuery({ coachOrgId: orgId })
  const videosLoading = isLoading || videoData?.isLoading
  const videos = videosLoading || videoData === null ? {} : videoData
  const { videoId } = workout
  const vid = videos?.[videoId]
  const readableTime = getReadableTime(vid?.duration)
  return <WorkoutDurationBox duration={readableTime} isLoading={videosLoading} isVidWkt={true} />
}

function WorkoutDurationBox({
  duration,
  isEditable = false,
  onPressEdit,
  isLoading = false,
  hasLowConfidenceEx,
  lowConfMultiWktIdxs = [],
  isMultiWkt,
  isVidWkt,
  isScriptDurationSource,
  isLowConfidence,
  lowConfidenceReason,
  tooShort = false,
}) {
  const warningState = getWarningState({ isScriptDurationSource, isLowConfidence, hasLowConfidenceEx })
  const tooltipText = tooShort
    ? 'Under 3 minutes, so members are not shown an estimate. Set a duration here if this is a real workout.'
    : getTooltipText({ lowConfMultiWktIdxs, isMultiWkt, isVidWkt, warningState, lowConfidenceReason })
  const tooltipOpen = tooShort || warningState !== 'none' || isMultiWkt || isVidWkt ? undefined : false
  return (
    <Tooltip
      content={
        <div className='max-w-[280px]' style={{ textWrap: 'balance' }}>
          {tooltipText}
        </div>
      }
      open={tooltipOpen}
    >
      <button
        data-testid='workoutDuration'
        className='py-1 px-1.5 rounded bg-white border border-gray-200 flex items-center group/child transition-colors'
        css={[isEditable && !isLoading && tw`cursor-pointer hover:border-tGreen`]}
        onClick={() => {
          if (isEditable && !isLoading && onPressEdit) {
            onPressEdit()
          }
        }}
        tabIndex={-1}
      >
        {isLoading ? (
          <div className='flex items-center'>
            <Skeleton className='w-10' />
          </div>
        ) : (
          <>
            <span className='font-semibold text-xs text-black'>{duration}</span>
            {warningState === 'strong' && <FiAlertTriangle className='w-3.5 h-3.5 text-red-500 ml-1' />}
            {warningState === 'soft' && <FiAlertTriangle className='w-3.5 h-3.5 text-orange-300 ml-1' />}
            {warningState === 'auto' && (
              <div className='flex items-center justify-center w-4 h-4 bg-gray-500 text-white ml-1 rounded-full leading-none text-[0.5rem] font-semibold'>
                AI
              </div>
            )}
          </>
        )}
      </button>
    </Tooltip>
  )
}

function getWarningState({ isScriptDurationSource, isLowConfidence, hasLowConfidenceEx }) {
  if (isScriptDurationSource && isLowConfidence) {
    return 'strong'
  } else if (isLowConfidence || hasLowConfidenceEx) {
    return 'soft'
  } else if (isScriptDurationSource && !isLowConfidence) {
    return 'auto'
  } else {
    return 'none'
  }
}

function getTooltipText({ lowConfMultiWktIdxs, isMultiWkt, isVidWkt, warningState, lowConfidenceReason }) {
  if (isVidWkt) {
    return 'Time estimate taken from video.'
  } else if (lowConfMultiWktIdxs?.length > 0) {
    return getMultiWktLowConfText({ lowConfMultiWktIdxs })
  } else if (isMultiWkt) {
    return 'To edit the day\u2019s total time, select one of its workouts.'
  } else if (warningState === 'strong') {
    // The calculation says WHY it distrusts the estimate — show the coach that
    // instead of a generic warning, so they know what to review.
    return lowConfidenceReason || 'Auto-generated time estimate needs review before publishing.'
  } else if (warningState === 'soft') {
    return lowConfidenceReason || 'Time estimate may be inaccurate. Review recommended.'
  } else if (warningState === 'auto') {
    return 'Estimate generated with AI'
  } else {
    return 'Auto time estimates need review.'
  }
}

function getMultiWktLowConfText({ lowConfMultiWktIdxs }) {
  const sortedIdxs = sortBy(lowConfMultiWktIdxs)
  const multiWktNumbers = sortedIdxs.map((idx) => idx + 1)

  let formattedWorkouts = ''

  if (multiWktNumbers.length === 1) {
    formattedWorkouts = `${multiWktNumbers[0]}`
  } else if (multiWktNumbers.length === 2) {
    formattedWorkouts = `${multiWktNumbers[0]} & ${multiWktNumbers[1]}`
  } else if (multiWktNumbers.length > 2) {
    formattedWorkouts = `${multiWktNumbers.slice(0, -1).join(', ')} & ${multiWktNumbers.at(-1)}`
  }

  const multiWktLowConfText = `Auto time estimates need review. Please review ${maybePluralize({ count: multiWktNumbers.length, text: 'workout' })} ${formattedWorkouts}.`
  return multiWktLowConfText
}

export default WorkoutDuration
